var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from "react";
import { AmbientLight, Color, DirectionalLight, Group, Mesh, MeshPhongMaterial, PerspectiveCamera, Scene, ShaderMaterial, SRGBColorSpace, Vector3, WebGLRenderer } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { busService } from "../../../../modules/bus";
import { AvatarLoadedMessage, LoadAvatarMessage } from "../../../../messages/rendering/RendererMessages";
import { AvatarStyle } from "flavr-common";
import { textureLoaderService } from "../../../../modules/rendering/texture/TextureLoaderService";
import { now } from "../../../../messages/timing";
import { AvatarHead } from "../../../../modules/avatar/AvatarHead";
import { AvatarShoulder } from "../../../../modules/avatar/AvatarShoulder";
import { AccessoireGroup, AvatarAccessoires } from "../../../../modules/avatar/AvatarAccessoires";
import { camera } from "../../../../modules/rendering/camera";
import { imageLoaderService } from "../../../../modules/rendering/image/ImageLoaderService";
import styled from "styled-components";
import { renderManager } from "../../../../modules/xr/RenderManager";
import { useMediaDevicesStore } from "../../../store/media-devices.store";
import { Text } from "troika-three-text";
import { WebcamBackgroundService } from "../../../../modules/webcam/WebcamBackgroundService";
import { configureWebcamBackground, getWebcamBackgroundMode } from "../../../../modules/webcam/WebcamBackgroundSettings";
import { preferencesService } from "../../../../modules/preferences/PreferencesService";
const connection = busService.connection("AvatarPreview");
const tmpPosition = new Vector3();
textureLoaderService.setPath("img/hills/");
const ENVIRONMENT_MAP = textureLoaderService.loadCube([
    "Hills_A_13_right.png",
    "Hills_A_13_left.png",
    "Hills_A_13_up.png",
    "Hills_A_13_down.png",
    "Hills_A_13_forward.png",
    "Hills_A_13_back.png"
]);
textureLoaderService.setPath("");
class Avatar {
    constructor(scene, uniforms, style, username) {
        this.scene = scene;
        this.uniforms = uniforms;
        this.style = style;
        this.username = username;
        this.isDestroyed = false;
        this.mainGroup = new Group();
        this.head = new AvatarHead(this.mainGroup);
        this.shoulder = new AvatarShoulder();
        this.accessoires = new AvatarAccessoires();
        this.loadVersion = 0;
        this.videoElement = undefined;
        this.cameraStream = undefined;
        this.processedCameraStream = undefined;
        this.webcamBackgroundService = new WebcamBackgroundService();
        this.cameraRequestVersion = 0;
        this.onAccessoirLoaded = (accessoir) => {
            this.head.setHairVisibility(!this.accessoires.shouldHideHair());
            if (accessoir.def.group === "hat") {
                accessoir.group.traverse(this.applyHatGroupUniforms);
            }
            else {
                accessoir.group.traverse(this.applyShoulderGroupUniforms);
            }
        };
        this.onAccessoirDestroyed = () => {
            this.head.setHairVisibility(!this.accessoires.shouldHideHair());
        };
        this.applyBaseUniforms = (obj) => {
            if (!(obj instanceof Mesh))
                return;
            if (Array.isArray(obj.material))
                return;
            if (!(obj.material instanceof ShaderMaterial))
                return;
            if (obj.material.uniforms.cameraCenterWorldPosition !== undefined) {
                obj.material.uniforms.cameraCenterWorldPosition = this.uniforms.cameraCenterWorldPosition;
            }
            if (obj.material.uniforms.thickness !== undefined) {
                obj.material.uniforms.thickness = this.uniforms.thickness;
            }
            if (obj.material.uniforms.worldScale !== undefined) {
                obj.material.uniforms.worldScale = this.uniforms.worldScale;
            }
        };
        this.applyMainUniforms = (obj) => {
            if (!(obj instanceof Mesh))
                return;
            if (Array.isArray(obj.material))
                return;
            this.applyBaseUniforms(obj);
            if (obj.material instanceof ShaderMaterial) {
                if (obj.material.uniforms.baseColor !== undefined) {
                    obj.material.uniforms.baseColor = { value: new Color(1, 1, 1) };
                    obj.material.uniforms.strokeColor = { value: new Color(0, 0, 0) };
                }
            }
            else if (obj.material instanceof MeshPhongMaterial) {
                obj.material.color = this.uniforms.baseColor.value;
            }
        };
        this.applyHatGroupUniforms = (obj) => {
            if (!(obj instanceof Mesh))
                return;
            if (Array.isArray(obj.material))
                return;
            this.applyBaseUniforms(obj);
            if (obj.material instanceof MeshPhongMaterial) {
                obj.material.color = this.uniforms.hatGroupColorDarkened.value;
                obj.material.emissive = this.uniforms.hatGroupColorDarkened.value;
            }
        };
        this.applyShoulderGroupUniforms = (obj) => {
            if (!(obj instanceof Mesh))
                return;
            if (Array.isArray(obj.material))
                return;
            this.applyBaseUniforms(obj);
            if (obj.material instanceof MeshPhongMaterial) {
                obj.material.color = this.uniforms.shoulderGroupColorDarkened.value;
                obj.material.emissive = this.uniforms.shoulderGroupColorDarkened.value;
            }
        };
        this.shoulder.setVisible(true);
        this.mainGroup.position.set(0, -0.1, 0);
        this.scene.add(this.shoulder.group);
        this.scene.add(this.mainGroup);
        this.head.userImage = imageLoaderService.loadTexture("/img/avatars/defaultUserImageNew.png");
    }
    destroy() {
        var _a, _b;
        this.isDestroyed = true;
        (_a = this.unsubscribeMediaDevices) === null || _a === void 0 ? void 0 : _a.call(this);
        this.unsubscribeMediaDevices = undefined;
        (_b = this.unsubscribePreferences) === null || _b === void 0 ? void 0 : _b.call(this);
        this.unsubscribePreferences = undefined;
        this.removeMesh();
        if (this.shoulder.group.parent) {
            this.shoulder.group.removeFromParent();
        }
        if (this.mainGroup.parent) {
            this.mainGroup.removeFromParent();
        }
        this.destroyVideo();
    }
    removeMesh() {
        this.head.destroy();
        this.shoulder.destroy();
    }
    changeStyle(style) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.style === style)
                return;
            this.style = style;
            this.removeMesh();
            yield this.load();
        });
    }
    setUsername(username) {
        if (this.username === username)
            return;
        this.username = username;
        this.createAvatarName();
    }
    createAvatarName() {
        if (this.usernameMesh) {
            this.usernameMesh.geometry.dispose();
            if (Array.isArray(this.usernameMesh.material))
                this.usernameMesh.material.forEach((m) => {
                    m.dispose();
                });
            else
                this.usernameMesh.material.dispose();
            this.mainGroup.remove(this.usernameMesh);
        }
        this.usernameMesh = new Text();
        this.usernameMesh.text = this.username;
        this.usernameMesh.textAlign = "center";
        this.usernameMesh.anchorX = "center";
        this.usernameMesh.anchorY = "middle";
        // Don't fade out avatar name
        this.usernameMesh.matrixAutoUpdate = false;
        this.usernameMesh.visible = true;
        this.usernameMesh.position.copy(new Vector3(0, 0.3, 0));
        this.usernameMesh.rotateY(Math.PI);
        this.usernameMesh.scale.set(0.5, 0.5, 0.5);
        this.usernameMesh.updateMatrix();
        this.usernameMesh.outlineColor = 0x000000;
        this.usernameMesh.outlineWidth = 0.0025;
        this.usernameMesh.sync();
        this.mainGroup.add(this.usernameMesh);
    }
    load() {
        return __awaiter(this, void 0, void 0, function* () {
            const version = ++this.loadVersion;
            const response = yield connection.askByMessageType(new LoadAvatarMessage("Avatar", this.style), AvatarLoadedMessage);
            if (version !== this.loadVersion)
                return;
            if (this.isDestroyed)
                return;
            if (!this.scene)
                return;
            this.createAvatarName();
            const headMeshGroup = response.headMesh.clone();
            const shoulderMeshGroup = response.shoulderMesh.clone();
            const mutedIndicator = headMeshGroup.getObjectByName("IndicatorMuted");
            if (mutedIndicator) {
                mutedIndicator.visible = false;
            }
            const volumeIndicator = headMeshGroup.getObjectByName("IndicatorVolume");
            if (volumeIndicator) {
                volumeIndicator.visible = false;
            }
            headMeshGroup.traverse(this.applyMainUniforms);
            shoulderMeshGroup.traverse(this.applyMainUniforms);
            if (!this.unsubscribeMediaDevices) {
                this.unsubscribeMediaDevices = useMediaDevicesStore.subscribe((state, previousState) => {
                    if (state.cam.enabled !== previousState.cam.enabled ||
                        state.cam.deviceId !== previousState.cam.deviceId) {
                        void this.onCamEnabledChanged(state.cam.enabled);
                    }
                });
                const onPreferenceChanged = (key) => __awaiter(this, void 0, void 0, function* () {
                    if (key !== "useWebcamBlur" && key !== "webcamBackgroundMode" && key !== "webcamBackgroundImageId" &&
                        key !== "webcamBlurRadius" && key !== "webcamPersonThreshold" && key !== "webcamEdgeSoftness") {
                        return false;
                    }
                    void this.updateCameraBackground();
                    return true;
                });
                preferencesService.onPreferenceChanged.add(onPreferenceChanged);
                this.unsubscribePreferences = () => preferencesService.onPreferenceChanged.delete(onPreferenceChanged);
                if (useMediaDevicesStore.getState().cam.enabled) {
                    void this.onCamEnabledChanged(true);
                }
            }
            this.head.addMesh(headMeshGroup);
            this.shoulder.addMesh(shoulderMeshGroup);
            this.accessoires.onStyleChanged(this.style);
            for (const accessoire of this.accessoires.accessoires.values()) {
                const groupNode = this.getAccessoirGroupNode(accessoire.def.group);
                if (groupNode) {
                    groupNode.add(accessoire.group);
                }
            }
        });
    }
    onCamEnabledChanged(enabled) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!enabled) {
                this.head.userVideoElement = undefined;
                this.destroyVideo();
                return;
            }
            this.destroyVideo();
            const version = this.cameraRequestVersion;
            this.head.userVideoElement = undefined;
            const deviceId = useMediaDevicesStore.getState().cam.deviceId;
            let stream;
            let video;
            try {
                stream = yield window.navigator.mediaDevices.getUserMedia({
                    video: { deviceId, width: 320 },
                    audio: false
                });
                if (version !== this.cameraRequestVersion ||
                    this.isDestroyed ||
                    useMediaDevicesStore.getState().cam.enabled === false ||
                    useMediaDevicesStore.getState().cam.deviceId !== deviceId) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }
                video = document.createElement("video");
                video.id = `avatarPreview-${stream.id}`;
                video.autoplay = true;
                video.muted = true;
                video.playsInline = true;
                video.style.display = "none";
                document.body.appendChild(video);
                if (version !== this.cameraRequestVersion || this.isDestroyed) {
                    video.pause();
                    video.srcObject = null;
                    video.load();
                    document.body.removeChild(video);
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }
                this.cameraStream = stream;
                this.videoElement = video;
                yield this.updateCameraBackground();
            }
            catch (err) {
                if (video) {
                    video.pause();
                    video.srcObject = null;
                    video.load();
                    if (video.parentElement) {
                        document.body.removeChild(video);
                    }
                }
                stream === null || stream === void 0 ? void 0 : stream.getTracks().forEach((track) => track.stop());
                console.error(err);
            }
        });
    }
    updateCameraBackground() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const stream = this.cameraStream;
            const video = this.videoElement;
            if (!stream || !video || this.isDestroyed)
                return;
            const version = ++this.cameraRequestVersion;
            this.webcamBackgroundService.stop();
            (_a = this.processedCameraStream) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((track) => track.stop());
            this.processedCameraStream = undefined;
            if (getWebcamBackgroundMode() === "original") {
                video.srcObject = stream;
                yield ((_b = video.play) === null || _b === void 0 ? void 0 : _b.call(video));
                if (version === this.cameraRequestVersion && !this.isDestroyed) {
                    this.head.userVideoElement = video;
                }
                return;
            }
            video.srcObject = null;
            this.head.userVideoElement = undefined;
            try {
                this.webcamBackgroundService.setVideoStream(stream);
                yield configureWebcamBackground(this.webcamBackgroundService, () => version === this.cameraRequestVersion && this.cameraStream === stream && !this.isDestroyed);
                if (version !== this.cameraRequestVersion || this.cameraStream !== stream || this.isDestroyed)
                    return;
                yield this.webcamBackgroundService.start();
                if (version !== this.cameraRequestVersion || this.cameraStream !== stream || this.isDestroyed)
                    return;
                this.processedCameraStream = this.webcamBackgroundService.getOutputStream();
                video.srcObject = this.processedCameraStream;
                yield ((_c = video.play) === null || _c === void 0 ? void 0 : _c.call(video));
                if (version === this.cameraRequestVersion && !this.isDestroyed) {
                    this.head.userVideoElement = video;
                }
            }
            catch (err) {
                if (version === this.cameraRequestVersion) {
                    this.webcamBackgroundService.stop();
                    console.error("Could not apply webcam background to avatar preview", err);
                }
            }
        });
    }
    destroyVideo() {
        this.cameraRequestVersion++;
        this.webcamBackgroundService.stop();
        if (this.processedCameraStream) {
            this.processedCameraStream.getTracks().forEach((track) => track.stop());
            this.processedCameraStream = undefined;
        }
        if (this.cameraStream) {
            this.cameraStream.getTracks().forEach((track) => {
                track.stop();
            });
            this.cameraStream = undefined;
        }
        if (this.videoElement) {
            this.videoElement.pause();
            this.videoElement.srcObject = null;
            this.videoElement.src = "";
            this.videoElement.load();
            document.body.removeChild(this.videoElement);
            this.videoElement = undefined;
        }
    }
    addAccessoire(id) {
        const accessoire = this.accessoires.add(id);
        if (!accessoire)
            return;
        accessoire.onLoadedDelegate.add(this.onAccessoirLoaded);
        accessoire.onDestroyedDelegate.add(this.onAccessoirDestroyed);
        const groupNode = this.getAccessoirGroupNode(accessoire.def.group);
        if (groupNode) {
            groupNode.add(accessoire.group);
        }
        accessoire.applyToTargetPoint(this.style);
        accessoire.load().catch((error) => {
            console.error(error);
        });
    }
    changeHatAccessoire(newId) {
        this.accessoires.removeByGroup(AccessoireGroup.Hat);
        if (newId === undefined || newId === -1) {
            return;
        }
        this.addAccessoire(newId);
    }
    changeShoulderAccessoire(newId) {
        this.accessoires.removeByGroup(AccessoireGroup.Shoulder);
        if (newId === undefined || newId === -1) {
            return;
        }
        this.addAccessoire(newId);
    }
    update() {
        // mirror rotation
        if (renderManager.isXRPresenting()) {
            const yaw = camera.perspectiveCam.rotation.x;
            const pitch = camera.perspectiveCam.rotation.z;
            this.mainGroup.rotation.set(yaw, 0, pitch);
        }
        this.mainGroup.updateMatrixWorld(true);
        this.shoulder.updateTransform(this.mainGroup.position, this.mainGroup.quaternion, this.mainGroup.scale);
    }
    getAccessoirGroupNode(group) {
        switch (group) {
            case "hat":
                return this.head.getMesh();
            case "shoulder":
                return this.shoulder.group;
        }
    }
}
const AvatarPreviewCanvas = styled.canvas({
    width: "180px",
    height: "212px"
});
export class LegacyAvatarPreview extends React.Component {
    constructor(props) {
        super(props);
        this.avatarStyle = AvatarStyle.Head;
        this.uniforms = {
            cameraCenterWorldPosition: { value: new Vector3() },
            worldScale: { value: 1 },
            time: { value: 0 },
            baseColor: { value: new Color(0x003333) },
            hatGroupColor: { value: new Color(0xff3333) },
            hatGroupColorDarkened: { value: new Color(0xff3333) },
            shoulderGroupColor: { value: new Color(0xff3333) },
            shoulderGroupColorDarkened: { value: new Color(0xff3333) },
            thickness: { value: 0.02 }
        };
        this.selectedShoulderAccessoireId = -1;
        this.selectedHatAccessoireId = -1;
        this.onPreviewRender = () => {
            if (!this.camera)
                return;
            if (!this.previewRenderer)
                return;
            if (!this.scene)
                return;
            this.camera.updateMatrixWorld();
            const worldPos = tmpPosition.setFromMatrixPosition(this.camera.matrixWorld);
            this.uniforms.cameraCenterWorldPosition.value.copy(worldPos);
            this.uniforms.time.value = now();
            if (this.avatar) {
                this.avatar.update();
            }
            this.previewRenderer.render(this.scene, this.camera);
        };
        this.componentDidMount = () => {
            if (!this.canvas) {
                return;
            }
            this.scene = new Scene();
            this.scene.background = ENVIRONMENT_MAP;
            this.scene.environment = ENVIRONMENT_MAP;
            this.camera = new PerspectiveCamera(55, 1, // this.canvas.clientWidth / this.canvas.clientHeight,
            0.1, 100);
            this.controls = new OrbitControls(this.camera, this.canvas);
            this.controls.screenSpacePanning = true;
            this.controls.minDistance = 0.75;
            this.controls.maxDistance = 2;
            const light = new DirectionalLight(0xf7f6ef, 1);
            light.position.set(0, 10, 10);
            this.scene.add(light);
            this.scene.add(new AmbientLight(0xffeec4, 0.3));
            this.camera.position.set(0, 0.1, -0.5);
            this.camera.matrixAutoUpdate = true;
            this.controls.target.set(0, 0, 0);
            this.controls.update();
            this.previewRenderer = new WebGLRenderer({ canvas: this.canvas });
            this.previewRenderer.outputColorSpace = SRGBColorSpace;
            this.previewRenderer.setClearColor(new Color(0, 0, 0.5));
            this.previewRenderer.setAnimationLoop(this.onPreviewRender);
            this.canvas.addEventListener("resize", this.resize);
            this.resize();
            this.avatar = new Avatar(this.scene, this.uniforms, this.avatarStyle, this.props.username);
            if (this.selectedShoulderAccessoireId !== -1) {
                this.avatar.changeShoulderAccessoire(this.selectedShoulderAccessoireId);
            }
            if (this.selectedHatAccessoireId !== -1) {
                this.avatar.changeHatAccessoire(this.selectedHatAccessoireId);
            }
            this.avatar.load().catch((error) => {
                console.error("AvatarPreview error", error);
            });
        };
        this.componentWillUnmount = () => {
            if (this.avatar) {
                this.avatar.destroy();
                this.avatar = undefined;
            }
            if (this.scene) {
                this.scene.traverse(this.disposeNode);
            }
            if (this.previewRenderer) {
                this.previewRenderer.dispose();
                this.previewRenderer = undefined;
            }
            if (this.controls) {
                this.controls.dispose();
                this.controls = undefined;
            }
            if (this.canvas) {
                this.canvas.removeEventListener("resize", this.resize);
            }
        };
        this.componentDidUpdate = (previousProps) => {
            var _a;
            if (previousProps.username !== this.props.username) {
                (_a = this.avatar) === null || _a === void 0 ? void 0 : _a.setUsername(this.props.username);
            }
        };
        this.resize = () => {
            var _a, _b;
            if (((_a = this.canvas) === null || _a === void 0 ? void 0 : _a.parentElement) === undefined ||
                ((_b = this.canvas) === null || _b === void 0 ? void 0 : _b.parentElement) === null ||
                this.previewRenderer === undefined ||
                this.camera === undefined)
                return;
            const width = this.canvas.parentElement.offsetWidth;
            const height = this.canvas.parentElement.offsetHeight;
            this.previewRenderer.setSize(width, height);
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
        };
        this.disposeNode = (obj) => {
            if (!(obj instanceof Mesh))
                return;
            if (obj.geometry !== undefined) {
                obj.geometry.dispose();
            }
            if (obj.material !== undefined) {
                if (obj.material.map) {
                    obj.material.map.dispose();
                }
                obj.material.dispose();
            }
        };
        this.state = { loading: true, error: undefined };
    }
    render() {
        const { avatarBaseColor, avatarHatGroup, avatarShoulderGroup, isShowingVRStyle } = this.props;
        if (isShowingVRStyle !== (this.avatarStyle === AvatarStyle.Head)) {
            this.avatarStyle = isShowingVRStyle ? AvatarStyle.Head : AvatarStyle.Monitor;
            if (this.avatar) {
                void this.avatar.changeStyle(this.avatarStyle);
            }
        }
        this.uniforms.baseColor.value.setHex(avatarBaseColor);
        this.uniforms.hatGroupColor.value.setHex(avatarHatGroup.color);
        let darkenedHatColor = new Color(avatarHatGroup.color);
        darkenedHatColor = darkenedHatColor.multiplyScalar(0.5);
        this.uniforms.hatGroupColorDarkened.value.setHex(darkenedHatColor.getHex());
        this.uniforms.shoulderGroupColor.value.setHex(avatarShoulderGroup.color);
        let darkenedShoulderColor = new Color(avatarShoulderGroup.color);
        darkenedShoulderColor = darkenedShoulderColor.multiplyScalar(0.5);
        this.uniforms.shoulderGroupColorDarkened.value.setHex(darkenedShoulderColor.getHex());
        if (this.selectedShoulderAccessoireId !== avatarShoulderGroup.id) {
            this.selectedShoulderAccessoireId = avatarShoulderGroup.id;
            if (this.avatar) {
                this.avatar.changeShoulderAccessoire(this.selectedShoulderAccessoireId);
            }
        }
        if (this.selectedHatAccessoireId !== avatarHatGroup.id) {
            this.selectedHatAccessoireId = avatarHatGroup.id;
            if (this.avatar) {
                this.avatar.changeHatAccessoire(this.selectedHatAccessoireId);
            }
        }
        return (React.createElement("div", { style: { width: "100%", height: "100%" } },
            (!this.props.avatarGltfUrl || (this.state.loading && !this.state.error)) && (React.createElement("div", { className: "loader" },
                React.createElement("div", { className: "loader-center" }))),
            this.props.avatarGltfUrl && this.state.error && !this.state.loading && (React.createElement("div", { className: "error" },
                React.createElement("img", { src: "hubs/src/assets/images/warning_icon.png", srcSet: "hubs/src/assets/images/warning_icon@2x.png 2x", className: "error-icon" }),
                React.createElement("div", { id: "avatar-preview.loading-failed" }))),
            React.createElement(AvatarPreviewCanvas, { ref: (c) => (this.canvas = c) })));
    }
}
